import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { WATER, heightAt } from "@/city3d/layout";
import { tickerLine } from "@/components/town/Ticker";
import { Casino } from "@/hud/Casino";
import { dur, fmt, useNow } from "@/hud/util";
import { EmergentBadge } from "@/hud/EmergentBadge";

export const CARD = "hud-panel rounded-[20px]";
const WARM = "var(--x-gold)";
const DIM = "var(--x-control)";

export const onX = (text, url = window.location.origin) =>
  `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;

export function Face({ p, size = 28, ring = "var(--x-surface)", dim = false }) {
  return <img src={p?.a} alt={p ? `@${p.h}` : ""} title={p ? `@${p.h}` : ""} className={`rounded-full bg-sky-light shrink-0 ${dim ? "grayscale opacity-60" : ""}`}
    style={{ width: size, height: size, boxShadow: `0 0 0 2px ${ring}` }} />;
}

export function Btn({ children, onClick, disabled, tone = "ink", className = "", type = "button" }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`chip-btn ${tone === "soft" ? "chip-soft" : ""} px-2.5 py-1 text-xs whitespace-nowrap ${className}`}>
      {children}
    </button>
  );
}

const XLink = ({ href, onClick, className = "", testId, children }) => (
  <a href={href} target="_blank" rel="noreferrer" onClick={onClick} data-testid={testId}
    className={`chip-btn chip-soft px-2.5 py-1.5 text-center text-xs whitespace-nowrap ${className}`}>{children}</a>
);
const Close = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-label="Close"
    className="hud-well grid h-7 w-7 place-items-center rounded-full text-x-mute transition-colors hover:bg-x-active hover:text-x-ink"><X className="h-4 w-4" /></button>
);
const Heading = ({ children }) => <div className="mb-1 mt-3 text-xs font-medium text-x-mute">{children}</div>;

export const utcTime = (iso) => `${new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit",
  minute: "2-digit", timeZone: "UTC" })} UTC`;

const FAMILIES = [["h", "Home", 1], ["m", "Mid-rise", 3], ["t", "Tower", 6]];

// Your own house: the one thing you can change, and the way out.
export function MyLadder({ l, onChanged, onLeft }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (path, body, after) => {
    setBusy(true);
    setErr(null);
    try {
      await api(path, { method: "POST", body });
      await after();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Heading>Your design</Heading>
      <div className="grid grid-cols-4 gap-1">
        {FAMILIES.flatMap(([f, name, need]) => [0, 1, 2, 3].map((i) => {
          const id = `${f}${i}`, locked = l.floors < need;
          return (
            <button key={id} type="button" disabled={busy || locked} title={locked ? `Needs ${need} floors` : undefined}
              onClick={() => run("/me/design", { design: id }, onChanged)}
              className={`rounded-md px-1 py-0.5 text-[11px] font-semibold transition hover:scale-105 disabled:opacity-40 ${l.design === id ? "acc-fill" : "bg-x-raised hover:bg-x-active"}`}>
              {name} {i + 1}
            </button>
          );
        }))}
      </div>
      {!l.uptown && <div className="mt-1 text-[11px] text-x-mute">On the Docks every house is a home. Taller designs go up once you move uptown.</div>}
      {err && <div className="mt-1 text-xs text-x-neg">{err}</div>}
      <div className="mt-2.5">
        <Btn tone="soft" disabled={busy} className="!text-x-neg"
          onClick={() => window.confirm("Remove your house from XField and sign out?") && run("/me/leave", undefined, onLeft)}>
          Remove my house
        </Btn>
      </div>
    </>
  );
}

// Who lives in a building that grew floors, top floor first, and which floor is this house's.
export function BuildingInfo({ b, k, share, onVisit }) {
  if (!b || b.size < 2) return null;
  const line = b.floor > 1 ? `Floor ${b.floor} of @${b.base.h}'s building` : `${b.size} people live on ${share ? "my" : "this"} building`;
  return (
    <div className="hud-well mt-2 rounded-[10px] px-2.5 py-2" data-testid="house-building">
      <div className="flex items-center justify-between gap-2 text-xs font-semibold">
        <span data-testid="house-building-line">{line}</span>
        {b.rank && <span className="shrink-0 text-x-mute tabular-nums" data-testid="house-building-rank">#{b.rank} tallest</span>}
      </div>
      <ol className="mt-1.5 flex max-h-56 flex-col gap-0.5 overflow-y-auto" data-testid="house-building-people">
        {b.people.map((p) => (
          <li key={p.k}>
            <button type="button" onClick={() => onVisit(p)} data-testid={`house-building-floor-${p.floor}`}
              className="flex w-full items-center gap-1.5 text-left text-[11px] hover:text-x-ink">
              <span className="w-6 shrink-0 font-semibold tabular-nums text-x-mute">F{p.floor}</span>
              <Face p={p} size={18} ring={p.s === "f" ? WARM : DIM} dim={p.s !== "f"} />
              <span className={`truncate ${p.k === k ? "font-semibold" : ""}`}>@{p.h}{p.floor === 1 ? " · ground" : ""}{p.s !== "f" ? " · dark" : ""}</span>
            </button>
          </li>
        ))}
      </ol>
      {share && <XLink href={onX(share.text, share.url)} className="mt-2 block" testId="share-building-button">Share my building on X</XLink>}
    </div>
  );
}

// A mansion on Millionaires' Row: which one, until when, and for its holder the card X will show.
export function MansionInfo({ m, mine }) {
  return (
    <div className="mt-2 rounded-[10px] bg-x-rose-soft px-2.5 py-2" data-testid="house-mansion">
      <div className="text-xs font-semibold" data-testid="house-mansion-line">Mansion #{m.rank} on Millionaires' Row · keys until {utcTime(m.until)}</div>
      {mine && (
        <>
          <img src={m.share.card_url} alt="How your mansion shows up on X" className="mt-2 w-full rounded-md border border-x-line"
            data-testid="mansion-card-preview" />
          <XLink href={onX(m.share.text, m.share.url)} className="mt-2 block" testId="share-mansion-button">Share my mansion on X</XLink>
        </>
      )}
    </div>
  );
}

const SHIPS = { rowboat: "rowboat", fishing: "fishing boat", cargo: "cargo ship", tanker: "tanker", liner: "cruise liner" };
const FLEX_NAMES = { statue: "a golden statue", helipad: "a rooftop helipad", spire: "a golden spire", yacht: "a superyacht" };
const DECREES = { docks_day: "Docks Day", fast_tide: "Fast Tide", fireworks: "Fireworks", tax_holiday: "Tax Holiday", lucky_cargo: "Lucky Cargo", duel_night: "Duel Night" };
const EVENTS = {
  big_ship: (e) => `@${e.h} brought in a ${SHIPS[e.ship] ?? "big ship"}`,
  warehouse: (e) => `@${e.h} built a warehouse on their dock`,
  floor: (e) => `@${e.h} built their tower up to ${e.f} floors`,
  flex: (e) => `@${e.h} bought ${FLEX_NAMES[e.what] ?? e.what}`,
  blimp: (e) => `@${e.h} launched the golden blimp (+25% ships for everyone)`,
  landmark: (e) => `@${e.h} sponsored ${e.what}`,
  lottery: (e) => `@${e.h} won the Harbour Lottery: ${fmt(e.uxd)} UXD`,
  top: (e) => (e.rank === 1 ? `@${e.h} is now the richest in XField` : `@${e.h} broke into the top 10 richest`),
  uptown: (e) => `@${e.h} rented a house uptown`,
  mansions: (e) => `Weekly handover: @${e.h} and the rest of the top ${e.n} earners moved into Millionaires' Row`,
  mayor: (e) => `@${e.h} is the new mayor`,
  duel: (e) => `@${e.h} won a duel${e.stake ? ` for ${fmt(e.stake)} UXD` : ""}`,
  decree: (e) => `Mayor @${e.h} decreed ${DECREES[e.kind] ?? e.kind}`,
  grant: (e) => `Mayor @${e.h} granted @${e.to} ${fmt(e.uxd)} UXD from the treasury`,
  jackpot: (e) => `@${e.h} hit the jackpot: ${fmt(e.uxd)} UXD from ${e.what === "crate" ? "a mystery crate"
    : e.what === "casino" ? "the Sea Casino" : `lucky cargo on a ${SHIPS[e.what] ?? "ship"}`}`,
  fireworks: (e) => `@${e.h} set off fireworks over their house`,
};
const eventLine = (e) => (EVENTS[e.type] ?? tickerLine)(e);

// The bottom ticker: what just happened in the city.
// The city's day turns over at midnight UTC.
function NewDay() {
  const day = 864e5, left = day - (useNow() % day);
  return (
    <span className="flex h-full shrink-0 items-center gap-1 border-l border-x-line px-3 tabular-nums text-x-mute" title="Trades, duels and the free duel start over at midnight UTC" data-testid="new-day">
      New day in<b className="font-semibold text-x-ink">{dur(left)}</b>
    </span>
  );
}

export function TickerBar({ events, people, live, onVisit }) {
  const seen = new Set();
  const items = events.map((e) => ({ text: eventLine(e), face: people[e.h?.toLowerCase()], go: e.h ? { k: e.h.toLowerCase() } : null }))
    .filter((it) => !seen.has(it.text) && seen.add(it.text));
  if (!items.length) return <EmergentBadge />;
  return (
    <div className="hud-panel pointer-events-auto absolute inset-x-0 bottom-0 flex h-9 items-center !border-x-0 !border-b-0 text-xs">
      <span className="flex h-full shrink-0 items-center gap-1.5 border-r border-x-line px-3 font-semibold"><span className="h-2 w-2 rounded-full bg-x-neg" />Live
        {live > 0 && <span className="font-medium tabular-nums text-x-mute" data-testid="live-count">· {fmt(live)} online</span>}</span>
      <div className="flex-1 overflow-hidden">
        <div className="marquee flex w-max" style={{ animationDuration: `${items.length * 5}s` }}>
          {[...items, ...items].map((it, i) => (
            <button key={i} type="button" onClick={() => it.go && onVisit(it.go)} className="flex items-center gap-1.5 whitespace-nowrap px-5 transition-colors hover:text-x-accent">
              {it.face && <Face p={it.face} size={18} />}
              <span className="font-medium">{it.text}</span>
            </button>
          ))}
        </div>
      </div>
      <NewDay />
      <EmergentBadge inline />
    </div>
  );
}

const PLACES = { timeline: "The Timeline", post: "Post Office", arrivals: "Arrivals", docks: "The Docks", towers: "Tallest buildings", row: "Millionaires' Row",
  aphelion: "Aphelion Labs", casino: "Sea Casino", park: "Central Park", market: "XField Market", hospital: "Hospital" };

// Buildings that grew floors, tallest first; equals by their ground owner's handle, as the server ranks them.
export const towersOf = (homes) => homes.filter((h) => h.level === 0 && h.stack.length > 1)
  .sort((a, b) => b.stack.length - a.stack.length || (a.owner.k < b.owner.k ? -1 : 1));

// What each building downtown is for.
export function PlaceCard({ id, town, city, me, eco, onVisit, onSignIn, onClose }) {
  if (!PLACES[id]) return null;
  const sp = town.landmarks?.[id];
  return (
    <div className={`${CARD} pop w-[min(92vw,360px)] p-3 pointer-events-auto`} data-testid={`place-card-${id}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{PLACES[id]}</h2>
        <Close onClick={onClose} testId="place-card-close" />
      </div>
      {sp && (
        <div className="mt-1 flex items-center justify-between rounded-xl bg-x-gold-soft px-2.5 py-1 text-xs font-semibold text-x-gold-ink" data-testid="place-sponsor">
          <span>Sponsored by <button type="button" onClick={() => { onVisit({ k: sp.k }); onClose(); }} className="underline">@{sp.h}</button></span>
        </div>
      )}
      <div className="max-h-[45vh] overflow-y-auto">
        {id === "timeline" && <Timeline events={town.ticker} />}
        {id === "post" && <PostOffice me={me} />}
        {id === "arrivals" && <Arrivals people={town.newest} onVisit={onVisit} />}
        {id === "docks" && <Docks town={town} me={me} />}
        {id === "towers" && <Towers town={town} homes={city.homes} me={me} onVisit={onVisit} />}
        {id === "row" && <Row town={town} me={me} onVisit={onVisit} />}
        {id === "aphelion" && <Aphelion />}
        {id === "casino" && <Casino eco={eco} onSignIn={onSignIn} />}
      </div>
    </div>
  );
}

// Aphelion Labs' office, the tallest building in town.
function Aphelion() {
  return (
    <>
      <div className="mt-2 rounded-[12px] bg-[#0F172A] px-4 py-3"><img src="/brands/aphelion.png" alt="Aphelion Labs" className="mx-auto h-12" /></div>
      <p className="mt-2 text-sm text-x-ink">
        The tallest building in XField. Aphelion Labs builds AI that takes real work off people's desks, and runs KindBench, a public
        benchmark for psychological safety in AI.
      </p>
      <XLink href="https://aphelion.in" className="mt-2 block" testId="aphelion-link">Visit aphelion.in</XLink>
    </>
  );
}

// Millionaires' Row: this week's top ten earners in their mansions, or who is in line before the first handover.
function Row({ town, me, onVisit }) {
  const row = town.mansions, byK = Object.fromEntries(town.residents.map((r) => [r.k, r]));
  const list = row ? row.holders.filter((h) => byK[h.k]).map((h) => ({ ...byK[h.k], rank: h.rank }))
    : town.residents.filter((r) => r.s === "f" && !r.out && r.wk > 0).sort((a, b) => b.wk - a.wk || (a.k < b.k ? -1 : 1)).slice(0, 10).map((p, i) => ({ ...p, rank: i + 1 }));
  const mine = row && me?.mansion;
  return (
    <>
      <div className="text-sm text-x-ink" data-testid="row-intro">
        {row ? `Week ${row.week}: the top 10 earners live here until ${utcTime(row.until)}.`
          : `The first handover is ${utcTime(town.next_handover)}. The top 10 earners of the week move in.`}
      </div>
      <Heading>{row ? "This week's holders" : "In line right now"}</Heading>
      <ol className="space-y-0.5" data-testid="row-list">
        {list.map((p) => (
          <li key={p.k}>
            <button type="button" onClick={() => onVisit(p)} data-testid={`row-holder-${p.rank}`}
              className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-sm transition-colors ${me?.k === p.k ? "bg-x-accent-soft" : "hover:bg-x-raised"}`}>
              <span className={`w-7 shrink-0 font-semibold tabular-nums ${p.rank === 1 ? "text-x-gold-ink" : "text-x-mute"}`}>#{p.rank}</span>
              <Face p={p} size={24} ring={WARM} />
              <b className="min-w-0 flex-1 truncate">@{p.h}</b>
              <span className="text-[11px] text-x-mute tabular-nums">+{fmt(p.wk)} UXD · {p.rank <= 2 ? "estate" : "villa"}</span>
            </button>
          </li>
        ))}
      </ol>
      {!list.length && <div className="text-sm text-x-mute">Nobody has earned anything this week yet.</div>}
      {mine && <XLink href={onX(mine.share.text, mine.share.url)} className="mt-2 block" testId="row-share-button">Share my mansion on X</XLink>}
      <div className="mt-2 text-[11px] text-x-mute">Every Monday at 9:00 UTC the whole row is handed out again. Earn UXD to move in.</div>
    </>
  );
}


const areaOf = (o) => (o.d != null ? "Docks" : o.s === "f" ? "Uptown" : "Dark lot");

// The city's tallest buildings, from the floors stacked on them once the ground filled up.
function Towers({ town, homes, me, onVisit }) {
  const list = towersOf(homes).slice(0, 20), { stats } = town;
  if (!list.length) {
    return <div className="text-sm text-x-ink" data-testid="towers-empty">No building has grown yet. Once all {stats.capacity} ground spots are taken, newcomers move in on top.</div>;
  }
  return (
    <>
      <div className="text-sm text-x-ink">
        {stats.floors} {stats.floors === 1 ? "floor" : "floors"} built on {stats.capacity} ground spots. Newcomers move in on top of whoever brought them, or the shortest building on the Docks.
      </div>
      <ol className="mt-2 space-y-1" data-testid="towers-list">
        {list.map((h, i) => {
          const mine = me && h.stack.some((p) => p.k === me.k), lit = h.stack.filter((p) => p.s === "f").length;
          return (
            <li key={h.owner.k}>
              <button type="button" onClick={() => onVisit(h.owner)} data-testid={`tower-row-${i + 1}`}
                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-sm transition-colors ${mine ? "bg-x-accent-soft" : "hover:bg-x-raised"}`}>
                <span className="w-5 shrink-0 font-semibold tabular-nums text-x-mute">{i + 1}.</span>
                <Face p={h.owner} size={26} ring={h.owner.s === "f" ? WARM : DIM} dim={h.owner.s !== "f"} />
                <span className="min-w-0 flex-1 leading-tight">
                  <b className="block truncate">@{h.owner.h}</b>
                  <span className="text-[11px] text-x-mute">{areaOf(h.owner)} · {lit} lit{mine ? " · you live here" : ""}</span>
                </span>
                <span className="flex -space-x-1.5">
                  {h.stack.slice(1).reverse().slice(0, 3).map((p) => <Face key={p.k} p={p} size={18} ring={p.s === "f" ? WARM : DIM} dim={p.s !== "f"} />)}
                </span>
                <span className="w-9 shrink-0 text-right font-semibold tabular-nums" data-testid={`tower-row-${i + 1}-size`}>
                  {h.stack.length}<span className="text-[11px] text-x-mute"> fl</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </>
  );
}

const Timeline = ({ events }) => (
  <>
    <Heading>Just happened</Heading>
    <ul className="space-y-1 text-xs">{events.slice(0, 12).map((e, i) => <li key={i}>{eventLine(e)}</li>)}</ul>
  </>
);

function Docks({ town, me }) {
  const docked = town.residents.filter((r) => r.d != null).length, l = me?.ladder;
  return (
    <div className="text-sm text-x-ink">
      <div>Every dock starts here. {docked} {docked === 1 ? "house is" : "houses are"} on the Docks right now.</div>
      <div className="mt-1">Rent a lot uptown by the week, near your friends. Lots by downtown cost more. Miss rent and you move back here; your ships keep working.</div>
      {l && <div className="mt-2 rounded-[10px] bg-x-accent-soft px-2.5 py-1.5 text-xs font-semibold tabular-nums text-x-accent-ink">{l.uptown ? "You're uptown" : `Your lot: ${fmt(l.line)} UXD a week`}</div>}
    </div>
  );
}

function PostOffice({ me }) {
  if (!me) return <div className="text-sm text-x-ink">Sign in to see who you know that hasn't moved in yet.</div>;
  const waiting = me.street.filter((p) => p.s === "r");
  if (!waiting.length) return <div className="text-sm text-x-ink">Everyone on your street has moved in.</div>;
  return (
    <>
      <div className="text-sm text-x-ink">These people's houses are waiting for them. A tag on X is how they find out.</div>
      <ul className="mt-2 space-y-1.5">
        {waiting.map((p) => (
          <li key={p.k} className="flex items-center gap-2 text-sm">
            <Face p={p} size={24} ring={DIM} dim />
            <span className="flex-1 min-w-0 truncate font-semibold">@{p.h}</span>
            <XLink href={onX(`@${p.h} your house in XField is waiting for you. Come move in →`, `${window.location.origin}/api/s/${p.h}`)}>Tag on X</XLink>
          </li>
        ))}
      </ul>
    </>
  );
}

const Arrivals = ({ people, onVisit }) => (
  <ul className="mt-1 space-y-1.5">
    {people.map((p) => (
      <li key={p.k}>
        <button type="button" onClick={() => onVisit(p)} className="w-full flex items-center gap-2 text-left text-sm hover:text-x-ink">
          <Face p={p} size={28} ring={WARM} />
          <b>@{p.h}</b>
        </button>
      </li>
    ))}
  </ul>
);

export function Minimap({ city, me, onPick }) {
  const ref = useRef();
  const [x0, x1, z0, z1] = city.island;
  const W = 200, scale = W / (x1 - x0 + 1), H = Math.round((z1 - z0 + 1) * scale);
  useEffect(() => {
    const ctx = ref.current.getContext("2d");
    const px = (x) => (x - x0) * scale, pz = (z) => (z - z0) * scale;
    const img = ctx.createImageData(W, H);
    const data = img.data;
    for (let py = 0; py < H; py++) {
      for (let x = 0; x < W; x++) {
        const h = heightAt(x0 + (x + 0.5) / scale, z0 + (py + 0.5) / scale, city);
        const i = (py * W + x) * 4;
        const c = h < WATER + 0.02 ? [76, 151, 209] : h < WATER + 0.05 ? [228, 210, 168] : h < 0.9 ? [125, 181, 99] : [94, 154, 72];
        data[i] = c[0]; data[i + 1] = c[1]; data[i + 2] = c[2]; data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    for (const p of city.pads) {
      ctx.fillStyle = p.color;
      ctx.fillRect(px(p.x - p.w / 2 + 0.5), pz(p.z - p.d / 2 + 0.5), p.w * scale, p.d * scale);
    }
    ctx.fillStyle = "#94A3B8";
    for (const r of city.roads) ctx.fillRect(px(r.x), pz(r.z), scale, scale);
    for (const h of city.homes) {
      ctx.fillStyle = h.color;
      ctx.fillRect(px(h.x) + 0.5, pz(h.z) + 0.5, scale - 1, scale - 1);
    }
    if (me) {
      ctx.strokeStyle = "#0F172A";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px(me[0] + 0.5), pz(me[1] + 0.5), 5, 0, Math.PI * 2);
      ctx.stroke();
    }
  }, [city, me, x0, z0, scale, W, H]);
  const click = (e) => {
    const b = e.currentTarget.getBoundingClientRect();
    onPick([x0 + ((e.clientX - b.left) / b.width) * (x1 - x0 + 1) - 0.5, z0 + ((e.clientY - b.top) / b.height) * (z1 - z0 + 1) - 0.5]);
  };
  return (
    <div className={`${CARD} hud-label p-1.5 pointer-events-auto`}>
      <canvas ref={ref} width={W} height={H} onClick={click} className="block rounded-xl cursor-pointer" style={{ width: W, height: H }} />
    </div>
  );
}
